import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BUDGET_GROUPS } from '@apleona/shared';
import { api } from '../api';
import { F, FormGrid, Modal } from './Modal';

export interface PersonFormValue {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  personnelNumber: string;
  costCenter: string;
  street: string;
  zip: string;
  city: string;
  driversLicenseNo: string;
  supervisorEmail: string;
  budgetGroup: string;
  companyId: string;
}

export const emptyPerson = (): PersonFormValue => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  personnelNumber: '',
  costCenter: '',
  street: '',
  zip: '',
  city: '',
  driversLicenseNo: '',
  supervisorEmail: '',
  budgetGroup: '',
  companyId: '',
});

export const personToForm = (p: Record<string, unknown>): PersonFormValue => ({
  ...emptyPerson(),
  id: p.id as string,
  firstName: (p.firstName as string) ?? '',
  lastName: (p.lastName as string) ?? '',
  email: (p.email as string) ?? '',
  phone: (p.phone as string) ?? '',
  personnelNumber: (p.personnelNumber as string) ?? '',
  costCenter: (p.costCenter as string) ?? '',
  street: (p.street as string) ?? '',
  zip: (p.zip as string) ?? '',
  city: (p.city as string) ?? '',
  driversLicenseNo: (p.driversLicenseNo as string) ?? '',
  supervisorEmail: (p.supervisorEmail as string) ?? '',
  budgetGroup: (p.budgetGroup as string) ?? '',
  companyId: ((p.company as { id?: string })?.id ?? '') as string,
});

export const PersonForm: React.FC<{
  open: boolean;
  initial: PersonFormValue;
  onClose: () => void;
}> = ({ open, initial, onClose }) => {
  const qc = useQueryClient();
  const [p, setP] = useState(initial);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setP(initial);
    setErr(null);
  }, [initial, open]);

  const { data: companies } = useQuery({
    queryKey: ['gesellschaften'],
    queryFn: () => api<{ id: string; name: string }[]>('/stammdaten/gesellschaften'),
    enabled: open,
  });

  const set = (x: Partial<PersonFormValue>) => setP((old) => ({ ...old, ...x }));

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...p, companyId: p.companyId || null };
      delete (body as { id?: string }).id;
      return p.id
        ? api(`/stammdaten/personen/${p.id}`, { method: 'PATCH', body: JSON.stringify(body) })
        : api('/stammdaten/personen', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['personen'] });
      onClose();
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <Modal
      open={open}
      title={p.id ? `Person bearbeiten – ${p.lastName}` : 'Neue Person anlegen'}
      onClose={onClose}
      wide
      footer={
        <>
          <span className="small muted" style={{ marginRight: 'auto' }}>Pflichtfeld: Nachname</span>
          <button className="btn" onClick={onClose}>Abbrechen</button>
          <button
            className="btn primary"
            disabled={p.lastName.trim().length < 1 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Speichern …' : 'Speichern'}
          </button>
        </>
      }
    >
      {err ? <div className="alert error">{err}</div> : null}
      <div className="alert info">
        Personen sind Fahrzeugnutzer – sie brauchen keinen App-Zugang. Wer sich in App oder
        Portal anmelden soll, wird unter <b>Benutzer</b> angelegt.
      </div>

      <FormGrid cols={3}>
        <div className="form-section">Person</div>
        <F label="Nachname *"><input value={p.lastName} onChange={(e) => set({ lastName: e.target.value })} /></F>
        <F label="Vorname"><input value={p.firstName} onChange={(e) => set({ firstName: e.target.value })} /></F>
        <F label="Personalnummer"><input value={p.personnelNumber} onChange={(e) => set({ personnelNumber: e.target.value })} /></F>

        <F label="E-Mail" hint="Erhält Protokolle in Kopie">
          <input type="email" value={p.email} onChange={(e) => set({ email: e.target.value })} />
        </F>
        <F label="Telefon"><input value={p.phone} onChange={(e) => set({ phone: e.target.value })} /></F>
        <F label="E-Mail Vorgesetzter">
          <input type="email" value={p.supervisorEmail} onChange={(e) => set({ supervisorEmail: e.target.value })} />
        </F>

        <div className="form-section">Zuordnung</div>
        <F label="Gesellschaft" span={2}>
          <select value={p.companyId} onChange={(e) => set({ companyId: e.target.value })}>
            <option value="">—</option>
            {(companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </F>
        <F label="Kostenstelle"><input value={p.costCenter} onChange={(e) => set({ costCenter: e.target.value })} /></F>

        <F label="Budgetgruppe">
          <select value={p.budgetGroup} onChange={(e) => set({ budgetGroup: e.target.value })}>
            <option value="">—</option>
            {BUDGET_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </F>
        <F label="Führerscheinnummer" span={2}>
          <input value={p.driversLicenseNo} onChange={(e) => set({ driversLicenseNo: e.target.value })} />
        </F>

        <div className="form-section">Anschrift</div>
        <F label="Straße und Hausnummer" span={2}>
          <input value={p.street} onChange={(e) => set({ street: e.target.value })} />
        </F>
        <F label="PLZ"><input value={p.zip} onChange={(e) => set({ zip: e.target.value })} /></F>
        <F label="Ort" span={2}><input value={p.city} onChange={(e) => set({ city: e.target.value })} /></F>
      </FormGrid>
    </Modal>
  );
};
